import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  FormGroup,
  Button,
} from "reactstrap";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Triangle } from "react-loader-spinner";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";
import ExpenseHeadingServices from "../../../Services/QRCodeServices/ExpenseHeadingServices/ExpenseHeadingServices";
import ExpenseHeadingAddEditModal from "../Components/ExpenseHeadingAddEditModal";

function ExpenseHeadingList() {
  const [expenseHeadings, setExpenseHeadings] = useState([]);
  const [filteredHeadings, setFilteredHeadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedHeading, setSelectedHeading] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  const toggleModal = () => setModal(!modal);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
    },
    {
      name: "Category",
      selector: (row) => row.categoryName,
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => row.description,
      sortable: true,
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div className="hstack gap-2">
          <Button
            color="btn btn-soft-warning"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <i className="ri-edit-line"></i>
          </Button>
          <Button
            color="btn btn-soft-danger"
            size="sm"
            onClick={() => handleDeleteClick(row)}
          >
            <i className="ri-delete-bin-line"></i>
          </Button>
        </div>
      ),
    },
  ];

  async function fetchExpenseHeadings() {
    try {
      setLoading(true);
      const fetchedHeadings = await ExpenseHeadingServices.getList(); // Hypothetical service call
      console.log("🚀 ~ fetchExpenseHeadings ~ fetchedHeadings:", fetchedHeadings)
      setExpenseHeadings(fetchedHeadings);
      setFilteredHeadings(fetchedHeadings);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching expense headings:", err);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchExpenseHeadings();
  }, []);

  useEffect(() => {
    let filtered = expenseHeadings;

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (heading) =>
          heading.title?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    setFilteredHeadings(filtered);
  }, [searchTerm, expenseHeadings]);

  const handleEdit = (row) => {
    setIsEdit(true);
    setSelectedHeading(row);
    toggleModal();
  };

  const handleCreate = () => {
    setIsEdit(false);
    setSelectedHeading(null);
    toggleModal();
  };

  const handleModalSuccess = () => {
    fetchExpenseHeadings();
    toggleModal();
  };

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await ExpenseHeadingServices.delete(selectedDeleteItem.id); // Hypothetical delete method
        toast.success("Expense heading deleted successfully");
        fetchExpenseHeadings();
      } catch (error) {
        toast.error("Failed to delete expense heading");
      } finally {
        toggleDeleteModal();
        setSelectedDeleteItem(null);
      }
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Expense Headings List"
            pageTitle="Expense Headings"
          />
          <Row>
            <Col>
              <Card>
                <CardHeader>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <FormGroup className="col-lg-3">
                      <Input
                        type="text"
                        placeholder="Search by Title"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                    <div className="ms-auto">
                      <Button color="primary" onClick={handleCreate}>
                        + Create Expense Heading
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={filteredHeadings}
                    customStyles={customStyles}
                    responsive
                    striped
                    highlightOnHover
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    progressPending={loading}
                    progressComponent={
                      <div className="my-3">
                        <Triangle
                          visible={true}
                          height="80"
                          width="80"
                          color="#5B71B9"
                          ariaLabel="triangle-loading"
                        />
                        <h5 className="mt-1">Loading...</h5>
                      </div>
                    }
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ExpenseHeadingAddEditModal
        isOpen={modal}
        toggle={toggleModal}
        isEdit={isEdit}
        selectedHeading={selectedHeading}
        onSuccess={handleModalSuccess}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={toggleDeleteModal}
        selectedItem={selectedDeleteItem}
      />
    </React.Fragment>
  );
}

export default ExpenseHeadingList;